import { ReactNode } from 'react'

import RichText from '@/components/ui/rich-text'
import LogoHorizontal from '@/icons/logo-horizontal.svg'

import { cx } from '@/utils/strings'

type PrintCoverProps = {
  title: string
  tagline?: string
  description?: ReactNode
  section?: ReactNode
  pageBreadcrumb?: Array<{ title: string; url?: string }>
  linkCtaText?: string
  pageUrl?: string
  backgroundClass?: string
  imageUrl?: string
}

export default function PrintCover({
  title,
  tagline,
  description,
  section,
  pageUrl,
  pageBreadcrumb,
  linkCtaText,
  backgroundClass = 'gradient-curve-base gradient-curve-1 opacity-80',
  imageUrl,
}: PrintCoverProps) {
  return (
    <div className="font-inter bg-white max-w-7xl mx-auto h-[calc(100vh)] [page-break-after: always]">
      <div className="p-16 overflow-hidden relative h-full">
        <div className="h-[calc(13vh)] text-primaryBlue fill-current absolute top-0 left-0 right-0 flex items-center z-10 pl-10">
          <div className="flex flex-row items-center justify-center gap-[18pt]">
            <LogoHorizontal className="w-[172pt] flex-[1 0 auto]" />
            {section && (
              <>
                <div className="w-[1.5pt] h-[38pt] bg-primaryBlue rounded-full" />
                <div className="text-[22pt]">{section}</div>
              </>
            )}
          </div>
        </div>
        <div className={cx('absolute top-0 left-0 z-5 h-full w-full', backgroundClass)}></div>
        <div
          className={cx(
            'absolute left-0 right-0 mx-8 mb-[24pt] z-10',
            imageUrl ? 'top-[200pt] pb-[150pt]' : 'bottom-5'
          )}
        >
          <div className="mx-4 mb-[20pt]">
            {(pageBreadcrumb?.length ?? 0) > 0 && (
              <div className="uppercase text-black text-[12pt] font-medium tracking-[18%] flex items-center mb-[7pt]">
                {pageBreadcrumb
                  ?.map((nav) => nav.title)
                  .reduce((prev, curr, index): any => [
                    prev,
                    <span key={index} className="font-bwi mx-[14pt] text-[8pt] rotate-180">
                      &#xe9a8;
                    </span>,
                    curr,
                  ])}
              </div>
            )}
            {tagline && (
              <h4 className="text-[12pt] font-medium tracking-[18%] uppercase text-black/80 my-2">{tagline}</h4>
            )}
            <h1 className="text-[60pt] leading-[1.1] mt-0">{title}</h1>
            {description}
            {imageUrl && (
              <img src={imageUrl} alt={title} className="w-1/2 object-cover rounded-4xl mt-[24pt] ml-auto" />
            )}
          </div>
          <div className="flex flex-wrap gap-[8pt] h-full px-[28pt] py-[12pt] text-black/60 text-[15pt] z-10 bg-white/80 rounded-full border border-bw-teal">
            {linkCtaText && <span className="font-semibold">{linkCtaText}</span>}
            <a href={pageUrl?.trim()} className="block text-black/60 break-all">
              {pageUrl}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
